import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, getDocs, getFirestore } from '@firebase/firestore';
import { app } from '../../firebase.js';
import LeftBar from '../../Components/LeftBar';
import Dashnav from '../../Components/Dashnav';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import Badge from 'react-bootstrap/Badge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowsRotate,
  faChartLine,
  faMagnifyingGlass,
  faUserCheck,
  faUsers
} from '@fortawesome/free-solid-svg-icons';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { toDateValue } from '../../utils/financeWorkflow';

const NAIROBI_TIME_ZONE = 'Africa/Nairobi';
const USER_COLLECTION = 'users';

const PENDING_STATUSES = new Set([
  'inactive',
  'pending',
  'applied',
  'application pending',
  'application',
  'applicant',
  'prospect',
  'prospective',
  'shortlisted',
  'waiting',
  'follow up',
  'follow-up'
]);

const JOINED_STATUSES = new Set([
  'student',
  'active',
  'completed',
  'paused',
  'deferred',
  'dropped',
  'expelled',
  'joined'
]);

const normalizeText = (value) => String(value || '').trim().toLowerCase();

const formatNumber = (value) => Number(value || 0).toLocaleString('en-KE');

const formatPercent = (value, digits = 1) => {
  if (!Number.isFinite(value)) {
    return '0%';
  }

  return `${value.toFixed(digits)}%`;
};

const getDateTimeValue = (value) => {
  const dateValue = toDateValue(value);
  return dateValue && !Number.isNaN(dateValue.getTime()) ? dateValue : null;
};

const getMonthKey = (dateValue) => {
  if (!dateValue) return '';
  return `${dateValue.getFullYear()}-${String(dateValue.getMonth() + 1).padStart(2, '0')}`;
};

const getMonthLabel = (dateValue) => {
  if (!dateValue) return 'Unknown month';
  return dateValue.toLocaleDateString('en-KE', {
    timeZone: NAIROBI_TIME_ZONE,
    month: 'short',
    year: 'numeric'
  });
};

const resolveCourse = (student = {}) => (
  student.course
  || student.program
  || student.programme
  || student.CourseName
  || 'Course not set'
);

const resolveCampus = (student = {}) => (
  student.branchName
  || student.campus
  || student.selectedCampus
  || student.classType
  || 'General'
);

const resolveStatusBucket = (student = {}) => {
  const status = normalizeText(student.userStatus || student.Status || student.status || '');

  if (status === 'did not join') {
    return 'didNotJoin';
  }

  if (JOINED_STATUSES.has(status)) {
    return 'joined';
  }

  if (PENDING_STATUSES.has(status) || !status) {
    return 'pending';
  }

  return 'unknown';
};

const getApplicationDate = (student = {}) => (
  getDateTimeValue(student.appDate)
  || getDateTimeValue(student.createdAt)
  || getDateTimeValue(student.admissionDate)
  || getDateTimeValue(student.classStartDate)
);

const buildTrendBucket = (bucket, monthLabel, monthKey) => {
  if (!bucket.has(monthKey)) {
    bucket.set(monthKey, {
      monthKey,
      monthLabel,
      year: Number(monthKey.slice(0, 4)),
      applicants: 0,
      joined: 0,
      pending: 0,
      didNotJoin: 0,
      unknown: 0
    });
  }

  return bucket.get(monthKey);
};

const buildYearBucket = (bucket, year) => {
  if (!bucket.has(year)) {
    bucket.set(year, {
      year,
      yearLabel: String(year),
      applicants: 0,
      joined: 0,
      pending: 0,
      didNotJoin: 0,
      unknown: 0
    });
  }

  return bucket.get(year);
};

const getTrendCoverage = (row) => {
  const tracked = row.joined + row.didNotJoin;
  return row.applicants > 0 ? (tracked / row.applicants) * 100 : 0;
};

const getTrendSuccess = (row) => {
  const tracked = row.joined + row.didNotJoin;
  return tracked > 0 ? (row.joined / tracked) * 100 : 0;
};

const trendTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  return (
    <div className="intakeChartTooltip">
      <strong>{label}</strong>
      {payload.map((entry) => (
        <div key={entry.dataKey} className="intakeChartTooltipRow">
          <span>{entry.name}</span>
          <strong>
            {entry.dataKey.includes('Rate') ? formatPercent(entry.value) : formatNumber(entry.value)}
          </strong>
        </div>
      ))}
    </div>
  );
};

function IntakeSuccessAnalysis() {
  const db = getFirestore(app);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('All');
  const [selectedYear, setSelectedYear] = useState('All');
  const [selectedMonthKey, setSelectedMonthKey] = useState('');
  const [students, setStudents] = useState([]);

  const fetchPage = useCallback(async () => {
    setRefreshing(true);

    try {
      const snapshot = await getDocs(collection(db, USER_COLLECTION));
      const rows = snapshot.docs
        .map((docItem) => {
          const data = docItem.data();
          const appDate = getApplicationDate(data);

          if (!appDate) {
            return null;
          }

          const course = resolveCourse(data);
          const campus = resolveCampus(data);
          const statusBucket = resolveStatusBucket(data);
          const monthKey = getMonthKey(appDate);

          if (!monthKey) {
            return null;
          }

          return {
            id: docItem.id,
            userId: data.userId || docItem.id,
            name: data.bio || data.displayName || data.name || 'Unnamed student',
            course,
            campus,
            appDate,
            monthKey,
            monthLabel: getMonthLabel(appDate),
            year: appDate.getFullYear(),
            userStatus: data.userStatus || data.Status || data.status || '',
            statusBucket,
            profilePic: data.profilePic || '',
            cohort: data.cohort || '',
            raw: data
          };
        })
        .filter(Boolean)
        .sort((first, second) => second.appDate.getTime() - first.appDate.getTime());

      setStudents(rows);
    } catch (error) {
      console.error('Failed to load intake success analysis:', error);
      setStudents([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [db]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  const availableCourses = useMemo(() => {
    return Array.from(new Set(students.map((student) => student.course).filter(Boolean))).sort();
  }, [students]);

  const availableYears = useMemo(() => {
    return Array.from(new Set(students.map((student) => String(student.year)).filter(Boolean)))
      .sort((left, right) => Number(left) - Number(right));
  }, [students]);

  const availableMonths = useMemo(() => {
    const seen = new Set();
    return students
      .slice()
      .sort((first, second) => first.appDate.getTime() - second.appDate.getTime())
      .filter((student) => {
        if (seen.has(student.monthKey)) {
          return false;
        }
        seen.add(student.monthKey);
        return true;
      });
  }, [students]);

  const filteredStudents = useMemo(() => {
    const queryText = searchTerm.trim().toLowerCase();

    return students.filter((student) => {
      if (selectedCourse !== 'All' && student.course !== selectedCourse) {
        return false;
      }

      if (selectedYear !== 'All' && String(student.year) !== selectedYear) {
        return false;
      }

      if (selectedMonthKey && student.monthKey !== selectedMonthKey) {
        return false;
      }

      if (!queryText) {
        return true;
      }

      const haystack = [
        student.name,
        student.course,
        student.campus,
        student.userStatus,
        student.cohort,
        student.monthLabel,
        student.year
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(queryText);
    });
  }, [searchTerm, selectedCourse, selectedMonthKey, selectedYear, students]);

  const analysis = useMemo(() => {
    const monthlyBucket = new Map();
    const yearlyBucket = new Map();

    filteredStudents.forEach((student) => {
      const monthRow = buildTrendBucket(monthlyBucket, student.monthLabel, student.monthKey);
      const yearRow = buildYearBucket(yearlyBucket, student.year);

      monthRow.applicants += 1;
      yearRow.applicants += 1;

      switch (student.statusBucket) {
        case 'joined':
          monthRow.joined += 1;
          yearRow.joined += 1;
          break;
        case 'pending':
          monthRow.pending += 1;
          yearRow.pending += 1;
          break;
        case 'didNotJoin':
          monthRow.didNotJoin += 1;
          yearRow.didNotJoin += 1;
          break;
        default:
          monthRow.unknown += 1;
          yearRow.unknown += 1;
          break;
      }
    });

    const monthlyRows = Array.from(monthlyBucket.values())
      .map((row) => ({
        ...row,
        tracked: row.joined + row.didNotJoin,
        successRate: getTrendSuccess(row),
        coverageRate: getTrendCoverage(row),
        pendingRate: row.applicants > 0 ? (row.pending / row.applicants) * 100 : 0,
        didNotJoinRate: row.applicants > 0 ? (row.didNotJoin / row.applicants) * 100 : 0
      }))
      .sort((first, second) => first.monthKey.localeCompare(second.monthKey));

    const yearlyRows = Array.from(yearlyBucket.values())
      .map((row) => ({
        ...row,
        tracked: row.joined + row.didNotJoin,
        successRate: getTrendSuccess(row),
        coverageRate: getTrendCoverage(row),
        pendingRate: row.applicants > 0 ? (row.pending / row.applicants) * 100 : 0,
        didNotJoinRate: row.applicants > 0 ? (row.didNotJoin / row.applicants) * 100 : 0
      }))
      .sort((first, second) => Number(first.year) - Number(second.year));

    const totalApplicants = filteredStudents.length;
    const totalJoined = filteredStudents.filter((student) => student.statusBucket === 'joined').length;
    const totalPending = filteredStudents.filter((student) => student.statusBucket === 'pending').length;
    const totalDidNotJoin = filteredStudents.filter((student) => student.statusBucket === 'didNotJoin').length;
    const totalUnknown = filteredStudents.filter((student) => student.statusBucket === 'unknown').length;
    const totalTracked = totalJoined + totalDidNotJoin;
    const successRate = totalTracked > 0 ? (totalJoined / totalTracked) * 100 : 0;
    const coverageRate = totalApplicants > 0 ? (totalTracked / totalApplicants) * 100 : 0;

    const latestYearRow = yearlyRows[yearlyRows.length - 1] || null;
    const previousYearRow = yearlyRows[yearlyRows.length - 2] || null;
    const yearDelta = latestYearRow && previousYearRow
      ? latestYearRow.successRate - previousYearRow.successRate
      : 0;

    const bestMonth = monthlyRows
      .filter((row) => row.tracked > 0)
      .sort((first, second) => second.successRate - first.successRate || second.tracked - first.tracked)[0] || null;

    return {
      monthlyRows,
      yearlyRows,
      totalApplicants,
      totalJoined,
      totalPending,
      totalDidNotJoin,
      totalUnknown,
      totalTracked,
      successRate,
      coverageRate,
      latestYearRow,
      previousYearRow,
      yearDelta,
      bestMonth
    };
  }, [filteredStudents]);

  const visibleMonthlyRows = useMemo(() => {
    if (analysis.monthlyRows.length <= 12) {
      return analysis.monthlyRows;
    }

    return analysis.monthlyRows.slice(-12);
  }, [analysis.monthlyRows]);

  const selectedMonthRow = useMemo(() => {
    if (!analysis.monthlyRows.length) {
      return null;
    }

    return analysis.monthlyRows.find((row) => row.monthKey === selectedMonthKey)
      || analysis.monthlyRows[analysis.monthlyRows.length - 1]
      || null;
  }, [analysis.monthlyRows, selectedMonthKey]);

  useEffect(() => {
    if (!analysis.monthlyRows.length) {
      if (selectedMonthKey) {
        setSelectedMonthKey('');
      }
      return;
    }

    if (selectedMonthKey && analysis.monthlyRows.some((row) => row.monthKey === selectedMonthKey)) {
      return;
    }

    if (!selectedMonthKey) {
      setSelectedMonthKey('');
    }
  }, [analysis.monthlyRows, selectedMonthKey]);

  const monthlyChartData = useMemo(() => {
    return visibleMonthlyRows.map((row) => ({
      ...row,
      successLabel: formatPercent(row.successRate),
      coverageLabel: formatPercent(row.coverageRate)
    }));
  }, [visibleMonthlyRows]);

  const yearlyChartData = useMemo(() => {
    return analysis.yearlyRows.map((row) => ({
      ...row,
      successLabel: formatPercent(row.successRate),
      coverageLabel: formatPercent(row.coverageRate)
    }));
  }, [analysis.yearlyRows]);

  const handleRefresh = useCallback(() => {
    fetchPage();
  }, [fetchPage]);

  const firstVisibleYear = availableYears[0] || '—';

  return (
    <div className="mainContainer">
      <LeftBar />
      <div className="theMainContainer">
        <Dashnav />
        <div className="clientsComp applicationsPage intakeSuccessPage">
          <section className="appsHeader">
            <div className="attendanceHeaderCopy">
              <p className="appsKicker">Data Analytics</p>
              <h4>Intake Success Trend</h4>
              <p className="appsSubtext">
                Borrowing the trend structure from the time-series dashboard, this view tracks how applications convert
                into joined students over time. Filter by course, year, and month, then compare success as a percentage.
              </p>
              <div className="attendanceHeaderTags">
                <span className="attendanceHeaderTag">Course filter</span>
                <span className="attendanceHeaderTag">Year filter</span>
                <span className="attendanceHeaderTag">Month filter</span>
              </div>
            </div>
            <div className="appsHeaderStats">
              <div className="appsStatChip">
                <span>Applications</span>
                <strong>{formatNumber(analysis.totalApplicants)}</strong>
              </div>
              <div className="appsStatChip">
                <span>Joined</span>
                <strong>{formatNumber(analysis.totalJoined)}</strong>
              </div>
              <div className="appsStatChip">
                <span>Pending</span>
                <strong>{formatNumber(analysis.totalPending)}</strong>
              </div>
              <div className="appsStatChip">
                <span>Success rate</span>
                <strong>{formatPercent(analysis.successRate)}</strong>
              </div>
              <div className="appsStatChip">
                <span>Coverage</span>
                <strong>{formatPercent(analysis.coverageRate)}</strong>
              </div>
            </div>
          </section>

          <section className="appsFiltersCard attendanceFiltersCard">
            <div className="appsSearch input-group attendanceSearch">
              <span className="input-group-text">
                <FontAwesomeIcon icon={faMagnifyingGlass} />
              </span>
              <Form.Control
                type="search"
                placeholder="Search student, course, month, campus, or status..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>

            <div className="attendanceToolbarRow">
              <Form.Group className="attendanceTypeControl">
                <Form.Label>Course</Form.Label>
                <Form.Select
                  className="filterSelect"
                  value={selectedCourse}
                  onChange={(event) => setSelectedCourse(event.target.value)}
                >
                  <option value="All">All courses</option>
                  {availableCourses.map((course) => (
                    <option key={course} value={course}>{course}</option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="attendanceTypeControl">
                <Form.Label>Year</Form.Label>
                <Form.Select
                  className="filterSelect"
                  value={selectedYear}
                  onChange={(event) => setSelectedYear(event.target.value)}
                >
                  <option value="All">All years</option>
                  {availableYears.map((year) => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="attendanceTypeControl">
                <Form.Label>Month</Form.Label>
                <Form.Select
                  className="filterSelect"
                  value={selectedMonthKey}
                  onChange={(event) => setSelectedMonthKey(event.target.value)}
                >
                  <option value="">All months</option>
                  {availableMonths.map((student) => (
                    <option key={student.monthKey} value={student.monthKey}>{student.monthLabel}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>

            <div className="incomeToolbarMeta attendanceMeta">
              <span className="incomeToolbarText">{filteredStudents.length} visible records</span>
              <span className="incomeInlineDot" />
              <span className="incomeToolbarText">Timeline starts at {firstVisibleYear}</span>
            </div>

            <Button variant="outline-dark" onClick={handleRefresh} disabled={refreshing}>
              <FontAwesomeIcon icon={faArrowsRotate} className="me-2" />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </section>

          <section className="attendanceMiniSummary">
            <div className="attendanceMiniSummaryItem">
              <span>Success</span>
              <strong>{formatPercent(analysis.successRate)}</strong>
            </div>
            <div className="attendanceMiniSummaryItem">
              <span>Coverage</span>
              <strong>{formatPercent(analysis.coverageRate)}</strong>
            </div>
            <div className="attendanceMiniSummaryItem">
              <span>Year growth</span>
              <strong>{formatPercent(analysis.yearDelta)}</strong>
            </div>
            <div className="attendanceMiniSummaryItem">
              <span>Month</span>
              <strong>{selectedMonthRow ? selectedMonthRow.monthLabel : 'All'}</strong>
            </div>
          </section>

          <section className="intakeOverviewRow">
            <article className="intakeOverviewCard">
              <span>Trend</span>
              <strong className={
                !analysis.latestYearRow || !analysis.previousYearRow
                  ? 'intakeTrendBadge intakeTrendBadge--flat'
                  : analysis.yearDelta > 0.5
                    ? 'intakeTrendBadge intakeTrendBadge--up'
                    : analysis.yearDelta < -0.5
                      ? 'intakeTrendBadge intakeTrendBadge--down'
                      : 'intakeTrendBadge intakeTrendBadge--flat'
              }>
                {
                  !analysis.latestYearRow || !analysis.previousYearRow
                    ? 'Need more years'
                    : analysis.yearDelta > 0.5
                      ? 'Growing'
                      : analysis.yearDelta < -0.5
                        ? 'Slowing'
                        : 'Flat'
                }
              </strong>
              <p>
                {analysis.latestYearRow && analysis.previousYearRow
                  ? `Latest year success is ${formatPercent(analysis.latestYearRow.successRate)} compared with ${formatPercent(analysis.previousYearRow.successRate)} the year before.`
                  : 'Add more historical data to see year-over-year change.'}
              </p>
            </article>

            <article className="intakeOverviewCard">
              <span>Best month</span>
              <strong>{analysis.bestMonth ? `${formatPercent(analysis.bestMonth.successRate)} success` : '—'}</strong>
              <p>
                {analysis.bestMonth
                  ? `${analysis.bestMonth.monthLabel} • ${formatNumber(analysis.bestMonth.applicants)} applications`
                  : 'No month with tracked outcomes yet.'}
              </p>
            </article>

            <article className="intakeOverviewCard">
              <span>Selected month</span>
              <strong>{selectedMonthRow ? formatPercent(selectedMonthRow.successRate) : '—'}</strong>
              <p>
                {selectedMonthRow
                  ? `${selectedMonthRow.monthLabel} • ${formatNumber(selectedMonthRow.applicants)} applications`
                  : 'Choose a month to inspect that period.'}
              </p>
            </article>
          </section>

          <section className="intakeChartGrid">
            <article className="appsTableCard intakeChartCard">
              <div className="appsTableHeader">
                <h6>
                  <FontAwesomeIcon icon={faChartLine} className="me-2" />
                  Monthly conversion
                </h6>
                <span>Success, coverage, and pending percentage</span>
              </div>
              <div className="intakeChartWrap">
                <ResponsiveContainer width="100%" height={420}>
                  <LineChart data={monthlyChartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="monthLabel" />
                    <YAxis tickFormatter={(value) => `${value}%`} />
                    <Tooltip content={trendTooltip} />
                    <Legend />
                    <Line type="monotone" dataKey="successRate" name="Success rate" stroke="#0f766e" strokeWidth={3} dot={{ r: 4 }} />
                    <Line type="monotone" dataKey="coverageRate" name="Coverage" stroke="#2563eb" strokeWidth={2.5} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="pendingRate" name="Pending" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </article>

            <article className="appsTableCard intakeChartCard">
              <div className="appsTableHeader">
                <h6>
                  <FontAwesomeIcon icon={faUsers} className="me-2" />
                  Yearly performance
                </h6>
                <span>Joined versus not joined by year</span>
              </div>
              <div className="intakeChartWrap">
                <ResponsiveContainer width="100%" height={420}>
                  <BarChart data={yearlyChartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="yearLabel" />
                    <YAxis />
                    <Tooltip content={trendTooltip} />
                    <Legend />
                    <Bar dataKey="applicants" name="Applications" fill="#cbd5e1" radius={[6, 6, 0, 0]} />
                    <Bar dataKey="joined" name="Joined" fill="#0f766e" radius={[6, 6, 0, 0]} />
                    <Bar dataKey="didNotJoin" name="Did not join" fill="#f97316" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </article>
          </section>

          <section className="appsTableCard intakeTableCard">
            <div className="appsTableHeader">
              <h6>
                <FontAwesomeIcon icon={faUserCheck} className="me-2" />
                Monthly success table
              </h6>
              <span>{analysis.monthlyRows.length} months</span>
            </div>

            <div className="appsTableWrap intakeTableWrap">
              {loading ? (
                <div className="incomeStateCard">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading intake success analysis...</span>
                  </Spinner>
                  <p>Loading intake success analysis...</p>
                </div>
              ) : (
                <Table hover responsive className="appsTable intakeTable">
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th>Applications</th>
                      <th>Joined</th>
                      <th>Pending</th>
                      <th>Did not join</th>
                      <th>Coverage</th>
                      <th>Success</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analysis.monthlyRows.map((row) => (
                      <tr key={row.monthKey}>
                        <td>
                          <div className="attendanceRecordCell">
                            <strong>{row.monthLabel}</strong>
                            <span>{row.year}</span>
                          </div>
                        </td>
                        <td>{formatNumber(row.applicants)}</td>
                        <td>{formatNumber(row.joined)}</td>
                        <td>{formatNumber(row.pending)}</td>
                        <td>{formatNumber(row.didNotJoin)}</td>
                        <td>
                          <Badge bg="light" text="dark" className="attendanceTypeBadge attendanceTypeBadge--both">
                            {formatPercent(row.coverageRate)}
                          </Badge>
                        </td>
                        <td>
                          <Badge bg="light" text="dark" className={
                            row.successRate >= 75
                              ? 'intakeSuccessBadge intakeSuccessBadge--high'
                              : row.successRate >= 50
                                ? 'intakeSuccessBadge intakeSuccessBadge--mid'
                                : 'intakeSuccessBadge intakeSuccessBadge--low'
                          }>
                            {formatPercent(row.successRate)}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                    {analysis.monthlyRows.length === 0 && (
                      <tr>
                        <td colSpan="7" className="appsEmptyState">
                          No records match the selected filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default IntakeSuccessAnalysis;
